import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Footer from '../components/Footer';

const DailyMediaDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [media, setMedia] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchMediaDetails();
  }, [id]);

  const fetchMediaDetails = async () => {
    try {
      const response = await fetch('/admin/api/daily-media.php');
      const data = await response.json();
      const mediaItem = data.find(item => item.id == id);
      
      if (mediaItem) {
        setMedia(mediaItem);
      } else {
        setError('Media not found');
      }
    } catch (error) {
      console.error('Error fetching media details:', error);
      setError('Failed to load media details');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 pt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (error || !media) {
    return (
      <div className="min-h-screen bg-gray-50 pt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="text-center">
            <div className="bg-white rounded-lg shadow-sm p-8">
              <div className="text-red-400 text-6xl mb-4">
                <i className="fas fa-exclamation-triangle"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Error</h3>
              <p className="text-gray-600 mb-4">{error || 'Media not found'}</p>
              <button
                onClick={() => navigate('/daily-media')}
                className="bg-teal-600 hover:bg-teal-700 text-white px-6 py-2 rounded-lg transition-colors"
              >
                Back to Daily Media
              </button>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pt-20">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <button
                onClick={() => navigate('/daily-media')}
                className="flex items-center text-teal-600 hover:text-teal-700 mb-2"
              >
                <i className="fas fa-arrow-left mr-2"></i>
                Back to Daily Media
              </button>
              <h1 className="text-3xl font-bold text-gray-900">{media.title}</h1>
              <p className="text-gray-600 mt-1">{media.category} • {formatDate(media.date)}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Media Display */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="relative">
                {media.media_type === 'image' ? (
                  <img
                    src={`admin/${media.file_path}`}
                    alt={media.title}
                    className="w-full h-auto max-h-96 object-cover"
                  />
                ) : (
                  <div className="w-full bg-gray-900">
                    <video
                      src={`admin/${media.file_path}`}
                      className="w-full h-auto max-h-96"
                      controls
                    />
                  </div>
                )}
                
                <div className="absolute top-4 left-4">
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                    media.media_type === 'image' 
                      ? 'bg-blue-100 text-blue-800' 
                      : 'bg-red-100 text-red-800'
                  }`}>
                    <i className={`fas fa-${media.media_type === 'image' ? 'image' : 'video'} mr-1`}></i>
                    {media.media_type}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Media Information */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 sticky top-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Media Information</h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                  <p className="text-gray-900 font-medium">{media.title}</p>
                </div>
                
                {media.description && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                    <p className="text-gray-600 text-sm leading-relaxed">{media.description}</p>
                  </div>
                )}
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                    <span className="inline-flex px-2 py-1 bg-teal-100 text-teal-800 rounded-full text-xs font-medium">
                      {media.category}
                    </span>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
                    <p className="text-gray-600 text-sm">{formatDate(media.date)}</p>
                  </div>
                </div>
                
                {media.location && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
                    <p className="text-gray-600 text-sm flex items-center">
                      <i className="fas fa-map-marker-alt mr-2 text-teal-600"></i>
                      {media.location}
                    </p>
                  </div>
                )}
                
                <div className="pt-4 border-t border-gray-200">
                  <a
                    href={`admin/${media.file_path}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full bg-teal-600 hover:bg-teal-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center justify-center"
                  >
                    <i className="fas fa-external-link-alt mr-2"></i>
                    View Full Size
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Related Media Section */}
        <div className="mt-12">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">More from {media.category}</h3>
            <p className="text-gray-600 text-sm">
              Explore more media from the same category to discover related school activities and events.
            </p>
            <div className="mt-4">
              <button
                onClick={() => navigate('/daily-media')}
                className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-lg text-sm font-medium transition-colors"
              >
                Browse All Media
              </button>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default DailyMediaDetails;
